import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Save, Building2, BarChart3, Plug, Scale, Database, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [company, setCompany] = useState(null);
  const [system, setSystem] = useState(null);
  const [demo, setDemo] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/settings/system").then((r) => setSystem(r.data)).catch(() => {});
    api.get("/settings/demo-data").then((r) => setDemo(r.data)).catch(() => {});
  }, []);

  const cleanupDemo = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/settings/cleanup-demo-data");
      const d = data.deleted;
      toast.success(`Removed ${d.leads} demo leads, ${d.employees} demo telecallers & ${d.related} related records`);
      const r = await api.get("/settings/demo-data"); setDemo(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCleaning(false); }
  };

  const saveCompany = async () => {
    try {
      await api.patch("/settings/company", company);
      toast.success("Company settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveTracking = async () => {
    try {
      await api.patch("/settings/system", { tracking: system.tracking });
      toast.success("Tracking settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!company || !system) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  const C = (k) => company[k] || "";
  const setC = (k, v) => setCompany({ ...company, [k]: v });
  const T = system.tracking || {};
  const setT = (k, v) => setSystem({ ...system, tracking: { ...T, [k]: v } });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Centralized company, tracking & integration configuration.</p>
      </div>

      <Tabs defaultValue="company">
        <TabsList className="bg-slate-900/60 border border-slate-800">
          <TabsTrigger value="company" data-testid="tab-company"><Building2 className="h-4 w-4 mr-1.5" />Company</TabsTrigger>
          <TabsTrigger value="tracking" data-testid="tab-tracking"><BarChart3 className="h-4 w-4 mr-1.5" />Meta Pixel</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations"><Plug className="h-4 w-4 mr-1.5" />Integrations</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal"><Scale className="h-4 w-4 mr-1.5" />Legal</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data"><Database className="h-4 w-4 mr-1.5" />Data</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <div className="glass rounded-xl p-6 grid md:grid-cols-2 gap-4">
            {[
              ["company_name", "Company Name"], ["tagline", "Tagline"], ["phone", "Phone"],
              ["email", "Email"], ["whatsapp", "WhatsApp"], ["website", "Website"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <Input value={C(k)} onChange={(e) => setC(k, e.target.value)} data-testid={`company-${k}`} className="bg-slate-900/60 border-slate-700 mt-1" />
              </div>
            ))}
            <div className="md:col-span-2">
              <Label className="text-xs text-muted-foreground">Address</Label>
              <Textarea value={C("address")} onChange={(e) => setC("address", e.target.value)} data-testid="company-address" className="bg-slate-900/60 border-slate-700 mt-1" />
            </div>
            <div className="md:col-span-2">
              <Button onClick={saveCompany} data-testid="save-company-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Company</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tracking" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4 max-w-lg">
            <div className="flex items-center justify-between rounded-lg border border-slate-800 p-3">
              <div><div className="text-sm font-medium">Enable Meta Pixel</div><div className="text-xs text-muted-foreground">Fires Lead event only after backend confirms creation.</div></div>
              <Switch checked={!!T.enabled} onCheckedChange={(v) => setT("enabled", v)} data-testid="tracking-enabled" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Pixel ID</Label>
              <Input value={T.pixel_id || ""} onChange={(e) => setT("pixel_id", e.target.value)} placeholder="e.g. 123456789012345" data-testid="tracking-pixel-id" className="bg-slate-900/60 border-slate-700 mt-1 font-mono" />
            </div>
            <Button onClick={saveTracking} data-testid="save-tracking-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Tracking</Button>
          </div>
        </TabsContent>

        <TabsContent value="integrations" className="mt-4">
          <div className="glass rounded-xl p-6 max-w-2xl space-y-3">
            {[
              ["Email — Resend", system.email?.provider, system.email?.enabled],
              ["AI Calling — Ringg", system.ai_provider?.provider, system.ai_provider?.enabled],
              ["WhatsApp — Deep Link", system.whatsapp?.mode, system.whatsapp?.enabled],
            ].map(([label, provider, enabled]) => (
              <div key={label} className="flex items-center justify-between rounded-lg border border-slate-800 p-4">
                <div>
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-xs text-muted-foreground font-mono">{provider || "—"}</div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${enabled ? "text-emerald-400 border-emerald-500/30" : "text-slate-400 border-slate-600"}`}>
                  {enabled ? "configured" : "pending"}
                </span>
              </div>
            ))}
            <p className="text-xs text-muted-foreground pt-2">Provider credentials are encrypted server-side and never returned to the browser. AI calling (Ringg) & email sequences activate in later modules.</p>
          </div>
        </TabsContent>

        <TabsContent value="legal" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4 max-w-3xl">
            <p className="text-xs text-muted-foreground">These power the public legal pages linked in the landing footer.</p>
            {[
              ["privacy_policy", "Privacy Policy"], ["terms", "Terms & Conditions"],
              ["disclaimer", "Disclaimer"], ["refund_policy", "Refund & Cancellation Policy"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <Textarea value={C(k)} onChange={(e) => setC(k, e.target.value)} data-testid={`legal-${k}`} rows={4} className="bg-slate-900/60 border-slate-700 mt-1" />
              </div>
            ))}
            <Button onClick={saveCompany} data-testid="save-legal-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Legal</Button>
          </div>
        </TabsContent>

        <TabsContent value="data" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-5 max-w-2xl">
            <div>
              <h3 className="font-semibold flex items-center gap-2"><Database className="h-4 w-4 text-primary" />Demo / Sample Data</h3>
              <p className="text-xs text-muted-foreground mt-1">The app ships with sample leads and demo telecaller accounts so you can explore it. Once you're live, remove them so only your real customers remain. This never touches real leads or your admin account.</p>
            </div>

            {demo?.already_cleaned ? (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-300" data-testid="demo-clean-state">
                <ShieldCheck className="h-4 w-4" /> Demo data has been cleared. Sample leads & telecallers won't be re-created on future deploys.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"><p className="text-xs text-muted-foreground">Demo leads</p><p className="text-2xl font-bold font-mono mt-1" data-testid="demo-leads-count">{demo?.demo_leads ?? "—"}</p></div>
                  <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"><p className="text-xs text-muted-foreground">Demo telecallers</p><p className="text-2xl font-bold font-mono mt-1" data-testid="demo-employees-count">{demo?.demo_employees ?? "—"}</p></div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" disabled={cleaning || (!demo?.demo_leads && !demo?.demo_employees)} data-testid="clear-demo-button" className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                      {cleaning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}Clear Demo Data
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-slate-950 border-slate-800">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove all demo data?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently deletes {demo?.demo_leads || 0} sample lead(s) and {demo?.demo_employees || 0} demo telecaller account(s) along with their activity, follow-ups and notifications. A demo telecaller is kept if any real lead is assigned to it. Your real leads and admin account are not affected. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-slate-700">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={cleanupDemo} data-testid="confirm-clear-demo" className="bg-red-600 hover:bg-red-700">Delete Demo Data</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <p className="text-[11px] text-muted-foreground">Tip: demo leads are the ones with <span className="font-mono">@example.com</span> emails. Real leads captured from your landing page are never matched.</p>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
